import ConsoleLog from './utils/ConsoleLog';
import ParameterStore from './parameters/ParameterStore';

const TAG = 'AttrDataUtil';
const WEB_LOG_BASE_ATTRIBUTE_NAME = 'data-web-log';
const WEB_LOG_EVENT_ATTRIBUTE = `${WEB_LOG_BASE_ATTRIBUTE_NAME}-event`;
const WEB_LOG_PARAM_ATTRIBUTE = `${WEB_LOG_BASE_ATTRIBUTE_NAME}-param`;

/**
 * Utility for attribute data of element.
 */
export default {
  /**
   * Set the event(data-web-log-event) value in the element.
   *
   * @param element {Element} element of document
   * @param eventType {string} type of event
   */
  setEvent(element: Element, eventType: string) {
    if (eventType) {
      element.setAttribute(WEB_LOG_EVENT_ATTRIBUTE, eventType);
      ConsoleLog.d(TAG, `#setEvent - eventType: ${eventType}`);
    } else {
      ConsoleLog.w(TAG, `#setEvent - eventType is empty`);
    }
  },

  /**
   * Get the event(data-web-log-event) value defined in element.
   *
   * @param element {Element} element of document
   * @returns {string} type of event
   */
  getEvent(element: Element): string {
    const eventType = element && element.getAttribute && element.getAttribute(WEB_LOG_EVENT_ATTRIBUTE) || '';
    ConsoleLog.d(TAG, `#getEvent - eventType${eventType ? `: ${eventType}` : ` is empty`}`);
    return eventType;
  },

  /**
   * Set the parameter(data-web-log-param) value in the element.
   *
   * @param element {Element} element of document
   * @param jsonData {object}
   */
  setRequestParam(element: Element, jsonData: RequestParams | string) {
    // We want to check this runtime because the SDK is consumed as the JS bundle.
    if (typeof jsonData !== 'string' && !this.isValidRequestParam(jsonData)) {
      throw new Error(`${TAG}: The value of data-web-log-param is not a valid format.`);
    }

    if (jsonData) {
      if (typeof jsonData === 'string' && jsonData.indexOf(ParameterStore.DYNAMIC_VALUE_FLAG) === 0) {
        element.setAttribute(WEB_LOG_PARAM_ATTRIBUTE, jsonData);
        ConsoleLog.d(TAG, `#setRequestParam - jsonData: ${jsonData}`);
      } else {
        const jsonString = JSON.stringify(jsonData);
        element.setAttribute(WEB_LOG_PARAM_ATTRIBUTE, jsonString);
        ConsoleLog.d(TAG, `#setRequestParam - jsonData: ${jsonString}`);
      }
    } else {
      ConsoleLog.w(TAG, `#setRequestParam - jsonData is empty`);
    }
  },

  /**
   * Get the parameter(data-web-log-param) value defined in the Element.
   *
   * @param element {Element} element of document
   * @returns {RequestParams|string}
   */
  getRequestParam(element: Element): RequestParams | string {
    const jsonString = element && element.getAttribute && element.getAttribute(WEB_LOG_PARAM_ATTRIBUTE) || '{}';
    if (jsonString.indexOf(ParameterStore.DYNAMIC_VALUE_FLAG) === 0) {
      ConsoleLog.d(TAG, `#getRequestParam resister parameter string - \n${jsonString}`);
      return jsonString;
    }

    ConsoleLog.d(TAG, `#getRequestParam - \n${jsonString}`);
    let jsonData: RequestParams;

    try {
      jsonData = JSON.parse(jsonString);
    } catch (e) {
      throw new Error(`${TAG}: The parameters are not in a correct format.`);
    }

    // We want to check this runtime because the SDK is consumed as the JS bundle.
    if (!this.isValidRequestParam(jsonData)) {
      throw new Error(`${TAG}: The value of data-web-log-param is not a valid format.`);
    }

    return jsonData;
  },

  /**
   * Returns false if the param object has incorrect keys or is empty.
   * We want to check this runtime because the SDK is consumed as the JS bundle in the legacy code, and
   * consumers using JS doesn't get alerted during the build time even if they pass a wrong parameter.
   * @param param {RequestParams | string}
   * @return {boolean}
   */
  isValidRequestParam(param: RequestParams): boolean {
    if (Object.keys(param).length === 0) {
      return true;
    }

    return !!(param.data || param.meta || param.extra);
  },

  /**
   * Get the component name of an element or ancestor. The name is determined in this order.
   * data-component-name
   * if not, id
   * if not and the element is button, button text.
   * @param elem {HTMLElement}
   * @return {string}
   */
  getComponentName(elem: HTMLElement): string {
    let name = '';
    const target = elem;

    if (elem.dataset?.componentName) {
      return elem.dataset.componentName;
    }

    if (elem.id) {
      return elem.id;
    }

    while (!name && elem.parentElement) {
      elem = elem.parentElement;

      if (elem.tagName === 'BODY') {
        break;
      }

      if (elem.dataset?.componentName) {
        name = elem.dataset.componentName;
        break;
      }
    }

    if (name === '') {
      if (target.tagName === 'BUTTON' && target.textContent) {
        return target.textContent.trim();
      }

      if (target.tagName === 'A' && (target as HTMLAnchorElement).href) {
        return (target as HTMLAnchorElement).href;
      }
    }

    return name;
  },

  /**
   * Get data-widget-name of the element or ancestor
   * @param elem {HTMLElement}
   * @return {string}
   */
  getWidgetName(elem: HTMLElement): string {
    return this.getAttributeValue(elem, 'data-widget-name');
  },

  /**
   * Get data-web-log-server-time of the element or ancestor
   * @param elem {HTMLElement}
   * @return {string}
   */
  getServerTime(elem: HTMLElement): string {
    return this.getAttributeValue(elem, 'data-web-log-server-time');
  },

  /**
   * Get the value of a given attribute of the element or ancestor
   * @param elem {HTMLElement}
   * @param attribute {string}
   * @return {string}
   */
  getAttributeValue(elem: HTMLElement, attribute: string): string {
    let name = '';

    while (!name && elem) {
      if (elem.tagName === 'BODY') {
        break;
      }

      if (elem.getAttribute(attribute)) {
        name = elem.getAttribute(attribute);
        break;
      }

      elem = elem.parentElement;
    }

    return name;
  },

  /**
   * Get data-web-log-trace-id of the element or ancestor.
   * @param elem {HTMLElement}
   * @return {string}
   */
  getTraceId(elem: HTMLElement): string {
    let id = '';

    while (!id && elem) {
      if (elem.tagName === 'BODY') {
        break;
      }

      if (elem.dataset?.webLogTraceId) {
        id = this.getCleanTraceId(elem.dataset.webLogTraceId);
        break;
      }

      elem = elem.parentElement;
    }

    return id;
  },

  /**
   * TraceId is a 32 character string. It can be transferred wrapped in other string like this.
   * 00-facf197bf9fe11086a9bf1953855173e-04202706c174e4ec-00
   * We only need the second part of this string.
   * @param traceId {string}
   * @return {string}
   */
  getCleanTraceId(traceId: string): string {
    const reg = /[\w\d]{32}/;
    const id = traceId.match(reg);

    return id === null ? '' : id[0];
  }
}
